import { takeEvery, all, call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import apiService from 'app/bff/services/apiService';
import { ReturnCodesEnum } from 'app/bff/enums/codes';
import { BaseResp } from 'app/bff/models/base';
import { PolicyPodetailResp } from 'app/bff/models/policy/policyPodetail';
import { AaOtpSendcodeResp } from 'app/bff/models/aa/aaOtpSendcode';
import { AaOtpVerifyResp } from 'app/bff/models/aa/aaOtpVerify';
import { PaymentCreditAuthResp } from 'app/bff/models/payment/paymentCreditAuth';
import { ROUTES } from 'app/core/router/paths';
import modalService from 'app/core/services/modalService';
import { ProcessRouteMatchesStep } from 'app/features/Service/QueryPolicy/Pay/types';
import { sagaBoundary } from 'app/store/service';
import { FetchPolicyDetailAction, SERVICE_QUERY_POLICY_PAY__FETCH_POLICY_DETAIL, SERVICE_QUERY_POLICY_PAY__SEND_OTP, SERVICE_QUERY_POLICY_PAY__RESEND_OTP, SERVICE_QUERY_POLICY_PAY__AUTH_OTP, SERVICE_QUERY_POLICY_PAY__EXECUTE_CREDIT_CARD_PAYMENT, SendOTPAction, ResendOTPAction, AuthOTPAction, ExecuteCreditCardPaymentAction } from './types';
import { fetchPolicyDetailDoneAction, setAccessibleStepAction, sendOTPDoneAction, resendOTPDoneAction } from './actions';

/**
 * @description 取得保單明細
 */
function * fetchPolicyDetail (action: FetchPolicyDetailAction) {
  const response: PolicyPodetailResp['policyTravel'] = yield call(apiService.postPolicyPodetail, action.payload.args);
  if (response) {
    // 取得保單明細 完成
    yield put(fetchPolicyDetailDoneAction(response));
    // 導頁至保單查詢我要繳費流程 (透過路由帶入資料)
    const state = { detail: response };
    yield put(push(ROUTES.SERVICE__QUERY_POLICY__PAY__CONFIRM_INFO, state));
  }
}

/**
 * @description 發送 OTP 驗證碼
 */
function * sendOTP (action: SendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 緩存 OTP 資料
    yield put(sendOTPDoneAction(response));
    // 將「確認資料」(Step-1-2) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.SERVICE__QUERY_POLICY__PAY__CONFIRM_INFO__OTP_AUTH]));
    // 導頁至「確認資料」(Step-1-2)
    yield put(push(ROUTES.SERVICE__QUERY_POLICY__PAY__CONFIRM_INFO__OTP_AUTH));
  }
}

/**
 * @description 重新發送 OTP 驗證碼
 */
function * resendOTP (action: ResendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 更新 OTP 資料
    yield put(resendOTPDoneAction(response));
  }
}

/**
 * @description 驗證 OTP 驗證碼
 */
function * authOTP (action: AuthOTPAction) {
  // 說明：若驗證 OTP 成功，則導頁至「線上繳費/繳費方式」(Step-2-1)。
  const response: AaOtpVerifyResp = yield call(apiService.postAaOtpVerify, action.payload.args);
  if (response) {
    // 將「線上繳費/繳費方式」(Step-2-1) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.SERVICE__QUERY_POLICY__PAY__PAYMENT]));
    // 導頁至「線上繳費/繳費方式」(Step-2-1)
    yield put(push(ROUTES.SERVICE__QUERY_POLICY__PAY__PAYMENT));
  }
}

/**
 * @description 執行信用卡繳費
 */
function * executeCreditCardPayment (action: ExecuteCreditCardPaymentAction) {
  const response: BaseResp<PaymentCreditAuthResp> = yield call(apiService.postPaymentCreditAuth, action.payload.args);
  if (response.respHeader.returnCode === ReturnCodesEnum.CodeSuccess) {
    // 將「繳費完成、繳費失敗」頁步驟 (Step-3) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.SERVICE__QUERY_POLICY__PAY__RESULT]));
    // 導頁至「繳費完成、繳費失敗」頁 (Step-3)
    yield put(push(ROUTES.SERVICE__QUERY_POLICY__PAY__RESULT));
  } else {
    modalService.base('系統提醒', response.transResp.payment.responseMsg);
  }
}

export default function * watchPaySaga () {
  yield all([
    takeEvery(SERVICE_QUERY_POLICY_PAY__FETCH_POLICY_DETAIL, sagaBoundary(fetchPolicyDetail)),
    takeEvery(SERVICE_QUERY_POLICY_PAY__SEND_OTP, sagaBoundary(sendOTP)),
    takeEvery(SERVICE_QUERY_POLICY_PAY__RESEND_OTP, sagaBoundary(resendOTP)),
    takeEvery(SERVICE_QUERY_POLICY_PAY__AUTH_OTP, sagaBoundary(authOTP)),
    takeEvery(SERVICE_QUERY_POLICY_PAY__EXECUTE_CREDIT_CARD_PAYMENT, sagaBoundary(executeCreditCardPayment))
  ]);
}
